"use client";

import { CheckCircleIcon, XCircleIcon, InfoIcon } from "@phosphor-icons/react";
import { useState } from "react";

import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerTrigger } from "@/src/components/shadcn/ui/drawer";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/src/components/shadcn/ui/tooltip";
import { getPersonName } from "@/src/models/person";
import { Practitioner } from "@/src/models/practitioner";
import {
  AgenticPractitionerRecommendation,
  AgenticRecommendation,
  Eligibility,
} from "@/src/models/scheduling-recommendation";

type PractitionerRecommendation = {
  npiNumber: string;
  name: string;
  isReceivingProvider: boolean;
  agentic?: AgenticPractitionerRecommendation;
};

const SCENARIOS = [
  { key: "newPatientScheduling", label: "New Patient" },
  { key: "establishedPatientScheduling", label: "Existing Patient" },
  { key: "rescheduling", label: "Rescheduling" },
] as const satisfies readonly { key: keyof AgenticPractitionerRecommendation; label: string }[];

function EligibilityIcon({ eligible }: { eligible: boolean }) {
  return eligible ? (
    <CheckCircleIcon className="h-3 w-3 text-success" />
  ) : (
    <XCircleIcon className="h-3 w-3 text-destructive" />
  );
}

function combineAndSortPractitioners(
  agenticRecommendation: AgenticRecommendation | null | undefined,
  receivingProvider: Practitioner | null | undefined,
  preferAgenticReceivingProvider: boolean
): PractitionerRecommendation[] {
  const agenticList = agenticRecommendation?.evaluatedPractitioners ?? [];
  const agenticReceivingNpi = preferAgenticReceivingProvider
    ? agenticList.find((practitioner) => practitioner.isReceivingProvider)?.npiNumber
    : null;
  const receivingNpi = agenticReceivingNpi ?? receivingProvider?.npiNumber;
  const byNpi = new Map(agenticList.map((p) => [p.npiNumber, p]));
  const allNpis = [...new Set([...agenticList.map((p) => p.npiNumber), ...(receivingNpi ? [receivingNpi] : [])])];

  const familyNameForSort = (name: string, person?: Practitioner | null) =>
    person?.familyName ?? name.trim().split(/\s+/).pop() ?? "";

  return allNpis
    .map((npi) => {
      const isReceiving = npi === receivingNpi;
      const isExtractedReceivingProvider = isReceiving && receivingProvider?.npiNumber === npi;
      const name = isExtractedReceivingProvider
        ? getPersonName(receivingProvider)
        : (byNpi.get(npi)?.providerDisplayName ?? "");

      return {
        npiNumber: npi,
        name,
        isReceivingProvider: isReceiving,
        agentic: byNpi.get(npi),
        familyNameForSort: familyNameForSort(name, isExtractedReceivingProvider ? receivingProvider : undefined),
      };
    })
    .sort((a, b) => {
      if (a.isReceivingProvider) return -1;
      if (b.isReceivingProvider) return 1;

      return a.familyNameForSort.localeCompare(b.familyNameForSort);
    });
}

function ReasonList({ reasons }: { reasons: string[] }) {
  return (
    <ul className="list-disc list-inside ml-2 text-xs text-muted-foreground space-y-1">
      {reasons.map((reason, idx) => (
        <li key={idx} className="mb-1 last:mb-0">
          {reason}
        </li>
      ))}
    </ul>
  );
}

function ReasonCodeBadges({ codes }: { codes: string[] }) {
  return (
    <div className="flex flex-wrap gap-1 ml-2">
      {codes.map((code) => (
        <span
          key={code}
          className="inline-flex items-center rounded-full border border-border px-2 py-0.5 text-[10px] font-medium text-muted-foreground"
        >
          {code}
        </span>
      ))}
    </div>
  );
}

function PractitionerCard({ practitioner, compact }: { practitioner: PractitionerRecommendation; compact: boolean }) {
  const agentic = practitioner.agentic;
  const scenarios = SCENARIOS.map((scenario) => ({ ...scenario, eligibility: agentic?.[scenario.key] })).filter(
    (scenario): scenario is typeof scenario & { eligibility: Eligibility } => scenario.eligibility != null
  );
  const hasScenarios = scenarios.length > 0;

  return (
    <div
      className={`flex flex-col gap-2 border-b pb-2 last:border-b-0 last:pb-0 ${
        compact ? "gap-3 border-background/20 pb-3" : "border-border"
      }`}
    >
      <div className="flex items-center gap-2">
        {agentic && !hasScenarios && (
          <span className="flex-shrink-0">
            <EligibilityIcon eligible={!!agentic.shouldSchedule} />
          </span>
        )}
        <span className={compact ? "font-semibold text-background" : "text-sm font-semibold text-foreground"}>
          {practitioner.name} {practitioner.isReceivingProvider ? "(Receiving Provider)" : ""}
        </span>
      </div>
      {hasScenarios ? (
        <div className="flex flex-col gap-1.5 ml-1">
          {scenarios.map(({ key, label, eligibility }) => (
            <div key={key} className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <span className="flex-shrink-0">
                  <EligibilityIcon eligible={eligibility.eligible} />
                </span>
                <span className={compact ? "text-xs text-background" : "text-xs font-medium text-foreground"}>
                  {label}
                </span>
              </div>
              {!compact && eligibility.reasons.length > 0 && <ReasonList reasons={eligibility.reasons} />}
              {!compact && eligibility.reasonCodes && eligibility.reasonCodes.length > 0 && (
                <ReasonCodeBadges codes={eligibility.reasonCodes} />
              )}
            </div>
          ))}
        </div>
      ) : (
        !compact && (
          <>
            {agentic?.reason && agentic.reason.length > 0 && <ReasonList reasons={agentic.reason} />}
            {agentic?.reasonCodes && agentic.reasonCodes.length > 0 && <ReasonCodeBadges codes={agentic.reasonCodes} />}
          </>
        )
      )}
    </div>
  );
}

export function SchedulingEligibility({
  agenticRecommendation,
  receivingProvider,
  preferAgenticReceivingProvider = false,
}: {
  agenticRecommendation?: AgenticRecommendation | null;
  receivingProvider?: Practitioner | null;
  preferAgenticReceivingProvider?: boolean;
}) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const combinedPractitioners = combineAndSortPractitioners(
    agenticRecommendation,
    receivingProvider,
    preferAgenticReceivingProvider
  );

  const openDrawer = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDrawerOpen(true);
    setTooltipOpen(false);
  };

  return (
    <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
      <TooltipProvider delayDuration={0}>
        <Tooltip open={tooltipOpen && !drawerOpen} onOpenChange={setTooltipOpen}>
          <TooltipTrigger asChild>
            <DrawerTrigger asChild>
              <button
                type="button"
                className="group size-4 inline-flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
                onClick={openDrawer}
                onMouseEnter={() => {
                  if (!drawerOpen) setTooltipOpen(true);
                }}
                onMouseLeave={() => setTooltipOpen(false)}
              >
                <InfoIcon className="size-4 opacity-70 group-hover:opacity-100 transition-opacity" />
              </button>
            </DrawerTrigger>
          </TooltipTrigger>
          <TooltipContent side="left" className="max-w-xs p-3" onClick={openDrawer}>
            <div className="flex flex-col gap-3">
              {combinedPractitioners.map((p) => (
                <PractitionerCard key={p.npiNumber} practitioner={p} compact />
              ))}
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <DrawerContent side="right" className="w-full max-w-2xl sm:w-3/4 flex flex-col p-0">
        <DrawerHeader className="flex-shrink-0 px-6 pt-6 pb-4">
          <DrawerTitle>Scheduling Eligibility</DrawerTitle>
        </DrawerHeader>
        <div className="flex-1 overflow-y-auto px-6 pb-6">
          <div className="flex flex-col gap-2">
            {combinedPractitioners.map((p) => (
              <PractitionerCard key={p.npiNumber} practitioner={p} compact={false} />
            ))}
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
